import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { IPublicDesktopV2OffersGetOffersByPinResponse } from 'shared/repositories/commercial-listing-map/desktop/v2/offers/get-offers-by-pin';
import { useActivePinListingIdSelector } from 'shared/store/clientSide/slices/mapControls/selectors/dumb';
import { FetchGetOffersByPinLoadService } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/offers/get-offers-by-pin/load';
import { FetchGetOffersLoadService } from 'shared/store/serverSide/commercial-search-offers/desktop/v2/offers/get-offers/load';
import { CardApiContext } from 'shared/types/CardApiContext';
import { CardContextData } from 'shared/types/CardContextData';

import { useRefetchClusters } from './internal/hooks/useRefetchClusters';
import { InitCardApiContext } from './types';

export function useInitListingByPinCardApi(): InitCardApiContext {
  const queryClient = useQueryClient();
  const listingPinId = useActivePinListingIdSelector();
  const fetchGetOffersByPinLoadService = useMemo(() => new FetchGetOffersByPinLoadService(queryClient), [queryClient]);
  const fetchGetOffersLoadService = useMemo(() => new FetchGetOffersLoadService(queryClient), [queryClient]);

  const refetchClustersOnSuccess = useRefetchClusters();

  return useCallback<(cardContextData: CardContextData) => CardApiContext>(
    cardContextData => ({
      addToFavorite: {
        onError: (_error, _variables, context: IPublicDesktopV2OffersGetOffersByPinResponse): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): IPublicDesktopV2OffersGetOffersByPinResponse | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: IPublicDesktopV2OffersGetOffersByPinResponse = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.extendedProperties.isFavorite = true;

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          /**
           * При работе в листинге по пину, добавив объявление в избранное, мы затем можем вернуться обратно на выдачу.
           * В таком случае мы не запрашиваем данные листинга снова, а используем кэш ReactQuery.
           * А значит, если в основном листинге есть то объявление, с которым мы работали, то оно покажет неправильное
           * значение "isFavorite".
           * Поэтому мы должны поменять флаг "isFavorite" у того оффера, с которым работали, руками и обновить кэш.
           */
          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, true);
        },
      },
      deleteFromFavorite: {
        onError: (_error, _variables, context: IPublicDesktopV2OffersGetOffersByPinResponse): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): IPublicDesktopV2OffersGetOffersByPinResponse | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: IPublicDesktopV2OffersGetOffersByPinResponse = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.extendedProperties.isFavorite = false;

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          /**
           * При работе в листинге по пину, удалив объявление из избранного, мы затем можем вернуться обратно на выдачу.
           * В таком случае мы не запрашиваем данные листинга снова, а используем кэш ReactQuery.
           * А значит, если в основном листинге есть то объявление, с которым мы работали, то оно покажет неправильное
           * значение "isFavorite".
           * Поэтому мы должны поменять флаг "isFavorite" у того оффера, с которым работали, руками и обновить кэш.
           */
          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, false);
        },
      },
    }),
    [fetchGetOffersByPinLoadService, fetchGetOffersLoadService, refetchClustersOnSuccess, listingPinId],
  );
}
